import React, { useState, useEffect, useMemo } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import CircularProgress from "@mui/material/CircularProgress";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Tooltip from "@mui/material/Tooltip";
import StarIcon from "@mui/icons-material/Star";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_logbook_entries_report,
  API_get_file_upload,
} from "../../../constants";
import { Lightbox } from "react-modal-image";
import { getPresetDateRange, getDateRangeDisplayLabel } from "./dateRangePresets";
import { formatDate } from "../requests/utilities/helpers";

const LogbookReportCard = ({ report, dateRangeOverride, homeStore, storeNames, businessWeekStartDayIndex }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);

  // Lightbox states
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadingFileEntryId, setLoadingFileEntryId] = useState(null);

  // Sorting states
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  // Get store names for display
  const getStoreNames = () => {
    if (!storeNames || !report.selected_stores) {
      return "No stores selected";
    }
    return storeNames
      .filter(store => report.selected_stores.includes(store.id))
      .map(store => store.store_name)
      .join(", ");
  };

  // Format date range for display
  const formatDateRangeForDisplay = (dateRangeString, customStartDate, customEndDate) => {
    if (dateRangeString === "custom") {
      if (customStartDate && customEndDate) {
        return `${customStartDate} to ${customEndDate}`;
      }
      return "Custom Range";
    }
    return getDateRangeDisplayLabel(dateRangeString);
  };

  // Format date for API calls
  const formatDate = (date) => {
    if (!date) return "";
    const jsDate = date instanceof Date ? date : new Date(date);
    const year = jsDate.getFullYear();
    const month = String(jsDate.getMonth() + 1).padStart(2, "0");
    const day = String(jsDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Table sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    const comparator = (a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case "store":
          aValue = a.store.name;
          bValue = b.store.name;
          break;
        case "category":
          aValue = a.category.name;
          bValue = b.category.name;
          break;
        case "author":
          aValue = a.author ? a.author.full_name : "";
          bValue = b.author ? b.author.full_name : "";
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    };

    return [...entries].sort(comparator);
  }, [entries, order, orderBy]);

  // Get file upload for lightbox
  const get_file_upload = async (file_upload_uuid, entry_id) => {
    setLoadingFileEntryId(entry_id);
    let api_params = {
      file_upload_uuid: file_upload_uuid,
    };
    try {
      const res = await getRightworkAPI(API_get_file_upload, api_params);
      if (res.status === 200) {
        setSelectedImage(res.data?.redux?.payload?.presigned_url);
        setShowLightbox(true);
      }
    } catch (error) {
      console.error("Error fetching file upload:", error);
    } finally {
      setLoadingFileEntryId(null);
    }
  };

  // Fetch logbook data
  const fetchLogbookData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Determine which date range to use
      let startDate, endDate;

      if (dateRangeOverride) {
        // Use dashboard override
        startDate = formatDate(dateRangeOverride.startDate);
        endDate = formatDate(dateRangeOverride.endDate);
      } else if (report.custom_start_date && report.custom_end_date) {
        // Use saved custom dates
        startDate = report.custom_start_date;
        endDate = report.custom_end_date;
      } else if (report.selected_date_range) {
        // Calculate preset date range
        const dateRange = getPresetDateRange({
          preset: report.selected_date_range,
          today: new Date(),
          businessWeekStartDayIndex: businessWeekStartDayIndex || 0,
        });
        startDate = formatDate(dateRange.startDate);
        endDate = formatDate(dateRange.endDate);
      }

      const params = {
        store_id: homeStore,
        store_ids: report.selected_stores?.join(",") || homeStore,
        start_date: startDate,
        end_date: endDate,
      };

      if (report.selected_categories?.length > 0) {
        params.category_uuids = report.selected_categories.join(",");
      }

      if (report.selected_authors?.length > 0) {
        params.author_user_ids = report.selected_authors.join(",");
      }

      if (report.search_query) {
        params.search_query = report.search_query;
      }

      const response = await getRightworkAPI(
        API_get_logbook_entries_report,
        params
      );

      const { entries, summary } = response.data.redux.payload;
      setEntries(entries || []);
      setSummary(summary || {});
    } catch (err) {
      console.error("Error fetching logbook data:", err);
      setError("Failed to load logbook data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogbookData();
  }, [report, dateRangeOverride]);

  // Format entry value - shows both numeric and text when both exist
  // Categories are mutable; an entry formats under the type it was RECORDED
  // with (the category's current type covers pre-backfill rows).
  const entryType = (entry) => entry.recorded_type || entry.category.type;

  const formatEntryValue = (entry) => {
    let numericValue = null;
    let numericDisplay = null;

    // Get numeric value if exists
    if (entry.value_integer !== null && entry.value_integer !== undefined && entry.value_integer !== "") {
      numericValue = parseInt(entry.value_integer);
    } else if (entry.value_float !== null && entry.value_float !== undefined && entry.value_float !== "") {
      numericValue = parseFloat(entry.value_float);
    }

    // Render numeric value based on type
    if (numericValue !== null) {
      if (entryType(entry) === "star") {
        // Render stars for star type
        numericDisplay = (
          <span className="flex items-center gap-0.5">
            {Array.from({ length: Math.min(numericValue, 5) }).map((_, i) => (
              <StarIcon key={i} sx={{ fontSize: 16, color: "#f59e0b" }} />
            ))}
          </span>
        );
      } else if (entryType(entry) === "percentage") {
        // Render number with % sign for percentage type
        numericDisplay = <span className="font-semibold text-sm">{numericValue}%</span>;
      } else if (entryType(entry) === "dollar") {
        // Render number with $ sign for dollar type
        numericDisplay = <span className="font-semibold text-sm">${numericValue}</span>;
      } else {
        // Render bold number for other types
        numericDisplay = <span className="font-semibold text-sm">{numericValue}</span>;
      }
    }

    // Render combined value
    if (numericDisplay && entry.value_text) {
      return (
        <div className="flex items-center gap-2">
          {numericDisplay}
          <span className="text-slate-600">-</span>
          <span className="text-sm whitespace-pre-line">{entry.value_text}</span>
        </div>
      );
    } else if (numericDisplay) {
      return numericDisplay;
    } else if (entry.value_text) {
      return <span className="text-sm whitespace-pre-line">{entry.value_text}</span>;
    } else {
      return "—";
    }
  };

  // Type badge colors
  const getTypeBadgeColor = (type) => {
    switch (type) {
      case "text":
        return "bg-blue-100 text-blue-700";
      case "star":
        return "bg-orange-100 text-orange-700";
      case "dollar":
        return "bg-green-100 text-green-700";
      case "percentage":
        return "bg-purple-100 text-purple-700";
      case "number":
        return "bg-rose-100 text-rose-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-6 flex flex-col h-full max-h-[900px]">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          {report.name}
        </h3>
        <div className="flex items-center justify-center flex-1">
          <CircularProgress size="1.5rem" sx={{ color: "#6d28d9" }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-6 flex flex-col h-full max-h-[900px]">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">
          {report.name}
        </h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center flex items-center justify-center flex-1">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showLightbox && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col h-full max-h-[900px]">
        {/* Card Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-lg font-semibold text-slate-800">{report.name}</h3>
            <div className="text-xs text-right">
              {dateRangeOverride ? (
                <div>
                  <span className="text-violet-600 font-medium">
                    {dateRangeOverride.type === "custom"
                      ? `${formatDate(dateRangeOverride.startDate)} to ${formatDate(dateRangeOverride.endDate)}`
                      : getDateRangeDisplayLabel(dateRangeOverride.type)}
                  </span>
                  <span className="ml-1 text-xxs text-gray-500">(Override)</span>
                </div>
              ) : (
                <span className="text-gray-500">
                  {formatDateRangeForDisplay(
                    report.selected_date_range,
                    report.custom_start_date,
                    report.custom_end_date
                  )}
                </span>
              )}
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-gray-600">{getStoreNames()}</p>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex-shrink-0">
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-slate-500">Total Entries:</span>{" "}
                <span className="font-semibold text-slate-800">
                  {summary.total_entries}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Categories:</span>{" "}
                <span className="font-semibold text-slate-800">
                  {summary.categories_included}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {entries.length === 0 ? (
          <div className="px-6 py-8 text-center flex-1 flex items-center justify-center">
            <p className="text-slate-500 text-sm">
              No entries found for this date range
            </p>
          </div>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    <TableSortLabel
                      active={orderBy === "created_at"}
                      direction={orderBy === "created_at" ? order : "asc"}
                      onClick={() => handleSort("created_at")}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    <TableSortLabel
                      active={orderBy === "store"}
                      direction={orderBy === "store" ? order : "asc"}
                      onClick={() => handleSort("store")}
                    >
                      Store
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    <TableSortLabel
                      active={orderBy === "category"}
                      direction={orderBy === "category" ? order : "asc"}
                      onClick={() => handleSort("category")}
                    >
                      Category
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", minWidth: 275 }}>
                    Value
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    <TableSortLabel
                      active={orderBy === "author"}
                      direction={orderBy === "author" ? order : "asc"}
                      onClick={() => handleSort("author")}
                    >
                      Author
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Files
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.map((entry) => (
                  <TableRow key={entry.uuid} hover>
                    <TableCell sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                      {entry.created_at_formatted}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {entry.store.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      <div className="flex items-center gap-1">
                        <span>{entry.category.name}</span>
                        {entry.category.description && (
                          <Tooltip
                            title={
                              <div className="whitespace-pre-line">
                                {entry.category.description}
                              </div>
                            }
                            arrow
                          >
                            <InfoOutlinedIcon
                              sx={{ fontSize: 14, color: "#64748b", cursor: "help" }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded ${getTypeBadgeColor(
                          entryType(entry)
                        )}`}
                      >
                        {entryType(entry)}
                      </span>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem", minWidth: 275 }}>
                      {formatEntryValue(entry)}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {entry.author ? (
                        <Tooltip
                          title={`${entry.author.permission_type} • ${entry.author.initials}`}
                        >
                          <span>{entry.author.full_name}</span>
                        </Tooltip>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {entry.file_uploads.length > 0 ? (
                        loadingFileEntryId === entry.uuid ? (
                          <div className="flex items-center justify-center">
                            <CircularProgress
                              size={16}
                              sx={{ color: "#6d28d9" }}
                            />
                          </div>
                        ) : (
                          <div
                            className="flex items-center justify-center gap-1 cursor-pointer hover:opacity-70"
                            onClick={() =>
                              get_file_upload(entry.file_uploads[0], entry.uuid)
                            }
                          >
                            <AttachFileIcon
                              sx={{ fontSize: 14, color: "#6d28d9" }}
                            />
                            <span className="text-xs text-violet-700">
                              {entry.file_uploads.length}
                            </span>
                          </div>
                        )
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </>
  );
};

export default LogbookReportCard;
